import { LocalStorageKey } from '@/lib/storageConstants'
import { NoJobDataError } from '@/modules/api/errors'
import {
    AdminCrowLink,
    AdminShopifyProduct,
    ApiClientSettings,
    ApiResponse,
    BulkUpdateBrandBenefitsRequest,
    CreateBrandGuidelineResponse,
    CreateCollectionResponse,
    CreateCrowLinkRequest,
    CreateCrowLinkResponse,
    CreateLandingPageRequestBody,
    CreateLandingPageResponse,
    CreatePlaylistResponse,
    CreateStorefrontsWithStorefrontsGeneratorResponse,
    CreateStorefrontsWithStorefrontsGeneratorV2Request,
    CreateStorefrontsWithStorefrontsGeneratorV2Response,
    CreateVideoInput,
    CreateVideoResponse,
    DeleteBrandGuidelineResponse,
    DeleteCollectionResponse,
    DeleteVideoResponse,
    DuplicateLandingPageRequestBody,
    DuplicateLandingPageResponse,
    ExploCustomerTokenResponse,
    ExploDashboardType,
    FullFunnelAggregationsResponse,
    GetAllCollectionsResponse,
    GetAllCrowLinksResponse,
    GetAllLandingPageLinksResponse,
    GetAllLandingPagesResponse,
    GetAllLandingPageTemplatesResponse,
    GetAllPlaylistsResponse,
    GetAllProductsOrderedByRelevanceToMetaAdRequest,
    GetAllProductsOrderedByRelevanceToMetaAdResponse,
    GetAllUsersResponse,
    GetAllVideosResponse,
    GetAuthenticatedSignatureResponse,
    GetAuthenticatedUploadURLResponse,
    GetAvailableClientsResponse,
    GetBrandBenefitsResponse,
    GetClientAllShopifyProductsRequest,
    GetClientAllShopifyProductsResponse,
    GetClientResponse,
    GetClientSettingsResponse,
    GetClientUnimportedShopifyProductsRequest,
    GetClientUnimportedShopifyProductsResponse,
    GetCollectionResponse,
    GetCrowLinksByLandingPageResponse,
    GetLandingPageLinkResponse,
    GetLandingPagePlaylistResponse,
    GetLandingPageResponse,
    GetPlaylistResponse,
    GetProfileResponse,
    GetTemplatesRankedInput,
    GetTemplatesRankedResponse,
    GetVideoResponse,
    ImportShopifyProductResponse,
    LoginResponse,
    MetaAd,
    MetaAdResponse,
    PerformanceInsightsResponse,
    Persona,
    RankedPersonaResult,
    RecentCrowLinkTestDataResponse,
    ResyncimagesForLandingPageLinkRequestBody,
    ResyncimagesForLandingPageLinkResponse,
    SearchGoogleFontsResponse,
    SlackChannelWithShareLink,
    StorefrontComparisonResponse,
    StorefrontMetricsResponse,
    UpdateBrandGuidelineResponse,
    UpdateClientInput,
    UpdateClientResponse,
    UpdateCollectionInput,
    UpdateCollectionResponse,
    UpdateLandingPageCartUpsellsBody,
    UpdateLandingPageCartUpsellsResponse,
    UpdateLandingPageContentRequestBody,
    UpdateLandingPageContentResponse,
    UpdateLandingPageLinkContentRequest,
    UpdateLandingPageLinkContentResponse,
    UpdateLandingPageLinkRequestBody,
    UpdateLandingPageLinkResponse,
    UpdateLandingPageLinksBody,
    UpdateLandingPageLinksResponse,
    UpdateLandingPageRequestBody,
    UpdateLandingPageResponse,
    UpdatePlaylistRequestBody,
    UpdatePlaylistResponse,
    UpdateVideoInput,
    UpdateVideoResponse,
    UploadImagesResponse,
} from '@storefronts/common-types'
import queryString from 'query-string'
import config from '../../config'
import { API } from './routes'

class Requestor {
    private static instance: Requestor

    client: string | null = null

    private constructor() {}

    public static getInstance(): Requestor {
        if (!Requestor.instance) {
            Requestor.instance = new Requestor()
        }
        return Requestor.instance
    }

    setClient(client: string) {
        this.client = client
    }

    getBearerToken(): string {
        const jwt = window.localStorage.getItem(LocalStorageKey.JWT)
        if (!jwt) {
            window.location.pathname = '/login'
        }
        return `Bearer ${jwt}`
    }

    checkResponseAuth(response: Response): void {
        if (response.status == 401) {
            window.location.pathname = '/login'
        }
    }

    get apiBaseUrl(): string {
        return `${config('API_BASEURL')}/api/${this.client}`
    }

    async makeAuthenticatedRequest<K>(
        path: string,
        queryParams: Record<string, unknown> | null = null,
        opts: RequestInit = {},
        pathOverride: boolean = false,
    ): Promise<K> {
        const qs =
            queryParams == null
                ? ''
                : queryString.stringify(queryParams, { arrayFormat: 'bracket' })
        let headers: HeadersInit = opts.headers || {}

        headers = {
            Authorization: this.getBearerToken(),
            ...headers,
        }

        let fullPath = `api/${this.client}/${path}?${qs}`
        if (pathOverride) {
            fullPath = path
        }

        const url = `${config('API_BASEURL')}/${fullPath}`

        // Handle request body for POST, PUT, and DELETE methods
        if (
            opts.method === 'post' ||
            opts.method === 'put' ||
            opts.method === 'delete'
        ) {
            opts.body = JSON.stringify(queryParams)
        }
        const resp = await fetch(url, {
            ...opts,
            headers: {
                'Content-Type': 'application/json',
                ...headers,
            },
        })

        const response = await resp.json()

        this.checkResponseAuth(resp)

        return response as K
    }

    async loginEmailPassword(
        email: string,
        password: string,
    ): Promise<LoginResponse> {
        const resp = await fetch(
            `${config('API_BASEURL')}/auth/login-email-pwd`,
            {
                method: 'post',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: email,
                    password,
                }),
            },
        )

        const response = await resp.json()

        if (response.statusCode == 401) {
            return {
                success: false,
                accessToken: null,
            }
        }

        if (response.isError) {
            throw new Error(response.message)
        }

        return {
            success: true,
            accessToken: response.result.accessToken,
        }
    }

    async getProfile(): Promise<GetProfileResponse> {
        const jwt = window.localStorage.getItem(LocalStorageKey.JWT)

        if (!jwt) {
            window.location.pathname = '/login'
        }

        const headers = {
            Authorization: `Bearer ${jwt}`,
            'Content-Type': 'application/json',
        }

        const resp = await fetch(
            `${config('API_BASEURL')}/${API.GetProfile()}`,
            {
                method: 'post',
                headers,
                body: JSON.stringify({}),
            },
        )

        const response = await resp.json()

        if (response.statusCode == 401) {
            return {
                isAuthenticated: false,
                user: null,
            }
        }

        if (response.isError) {
            throw new Error(response.message)
        }

        const res = response.result as GetProfileResponse

        if (!res.isAuthenticated) {
            return res
        }

        this.setClient(res.user.client?.clientHash)
        return res
    }

    async getAllVideos(): Promise<GetAllVideosResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetAllVideosResponse>
        >(API.GetAllVideos(), {}, { method: 'get' })

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async getAuthenticatedUploadURL(
        videoHash: string,
    ): Promise<GetAuthenticatedUploadURLResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetAuthenticatedUploadURLResponse>
        >(
            API.GetAuthenticatedUploadURL(),
            {
                videoHash,
            },
            { method: 'post' },
        )

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async createVideo(
        videoData: CreateVideoInput,
    ): Promise<CreateVideoResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<CreateVideoResponse>
        >(
            API.CreateVideo(),
            {
                ...videoData,
            },
            { method: 'post' },
        )

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getVideo(videoHash: string): Promise<GetVideoResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetVideoResponse>
        >(API.GetVideo(videoHash), {}, { method: 'get' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async updateVideo(
        videoHash: string,
        videoData: UpdateVideoInput,
    ): Promise<UpdateVideoResponse> {
        console.log(videoData)
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<UpdateVideoResponse>
        >(
            API.UpdateVideo(videoHash),
            {
                ...videoData,
            },
            { method: 'post' },
        )

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async deleteVideo(videoHash: string): Promise<DeleteVideoResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<DeleteVideoResponse>
        >(API.DeleteVideo(videoHash), {}, { method: 'delete' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getAllPlaylists(): Promise<GetAllPlaylistsResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetAllPlaylistsResponse>
        >(API.GetAllPlaylists(), {}, { method: 'get' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getPlaylist(playlistHash: string): Promise<GetPlaylistResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetPlaylistResponse>
        >(API.GetPlaylist(playlistHash), {}, { method: 'get' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getLandingPagePlaylist(
        playlistHash: string,
    ): Promise<GetLandingPagePlaylistResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetLandingPagePlaylistResponse>
        >(API.GetLandingPagePlaylist(playlistHash), {}, { method: 'get' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async createPlaylist(
        videoHashes: string[],
        name: string,
        thumbnailUrl?: string,
        bannerText?: string,
        isHidden?: boolean,
    ): Promise<CreatePlaylistResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<CreatePlaylistResponse>
        >(
            API.CreatePlaylist(),
            {
                videoHashes,
                name,
                thumbnailUrl,
                bannerText,
                isHidden,
            },
            { method: 'post' },
        )

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async updatePlaylist(
        playlistHash: string,
        playlistData: UpdatePlaylistRequestBody,
    ): Promise<UpdatePlaylistResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<UpdatePlaylistResponse>
        >(
            API.UpdatePlaylist(playlistHash),
            {
                ...playlistData,
            },
            { method: 'post' },
        )

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getAuthenticatedSignature(): Promise<GetAuthenticatedSignatureResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetAuthenticatedSignatureResponse>
        >(API.GetAuthenticatedUploadSignature(), {}, { method: 'get' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getClient(): Promise<GetClientResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetClientResponse>
        >(API.GetClient(), {}, { method: 'get' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async updateClient(
        clientData: UpdateClientInput,
    ): Promise<UpdateClientResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<UpdateClientResponse>
        >(
            API.UpdateClient(),
            {
                ...clientData,
            },
            { method: 'post' },
        )

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getAllUsers(): Promise<GetAllUsersResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetAllUsersResponse>
        >(API.GetAllUsers(), {}, { method: 'get' })

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async getAllCollections(): Promise<GetAllCollectionsResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetAllCollectionsResponse>
        >(API.GetAllCollections(), {}, { method: 'get' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getCollection(
        collectionHash: string,
    ): Promise<GetCollectionResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetCollectionResponse>
        >(API.GetCollection(collectionHash), {}, { method: 'get' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async createCollection(
        playlistHashes: string[],
        name: string,
        newOrderOfPlaylistHashes: string[],
        collectionBarColor?: string,
        playlistCircleBorderColor?: string,
        fontColor?: string,
    ): Promise<CreateCollectionResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<CreateCollectionResponse>
        >(
            API.CreateCollection(),
            {
                playlistHashes,
                name,
                newOrderOfPlaylistHashes,
                collectionBarColor,
                playlistCircleBorderColor,
                fontColor,
            },
            { method: 'post' },
        )

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async updateCollection(
        collectionHash: string,
        collectionData: UpdateCollectionInput,
    ): Promise<UpdateCollectionResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<UpdateCollectionResponse>
        >(
            API.UpdateCollection(collectionHash),
            {
                ...collectionData,
            },
            { method: 'post' },
        )

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async deleteCollection(
        collectionHash: string,
    ): Promise<DeleteCollectionResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<DeleteCollectionResponse>
        >(API.DeleteCollection(collectionHash), {}, { method: 'delete' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    // Landing Pages
    async getAllLandingPages(): Promise<GetAllLandingPagesResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetAllLandingPagesResponse>
        >(API.GetAllLandingPages(), {}, { method: 'get' })

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async getLandingPage(
        landingPageHash: string,
    ): Promise<GetLandingPageResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetLandingPageResponse>
        >(API.GetLandingPage(landingPageHash), {}, { method: 'get' })

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async getTemplatePreview(
        landingPageHash: string,
    ): Promise<GetLandingPageResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetLandingPageResponse>
        >(API.GetTemplatePreview(landingPageHash), {}, { method: 'get' })

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async getAllLandingPageTemplates(): Promise<GetAllLandingPageTemplatesResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetAllLandingPageTemplatesResponse>
        >(API.GetAllLandingPageTemplates(), {}, { method: 'get' })

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async createLandingPage(
        req: CreateLandingPageRequestBody,
    ): Promise<CreateLandingPageResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<CreateLandingPageResponse>
        >(
            API.CreateLandingPage(),
            {
                ...req,
            },
            { method: 'post' },
        )

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async duplicateLandingPage(
        req: DuplicateLandingPageRequestBody,
    ): Promise<DuplicateLandingPageResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<DuplicateLandingPageResponse>
        >(
            API.DuplicateLandingPage(),
            {
                ...req,
            },
            { method: 'post' },
        )

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async updateLandingPage(
        req: UpdateLandingPageRequestBody,
    ): Promise<UpdateLandingPageResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<UpdateLandingPageResponse>
        >(
            API.UpdateLandingPage(),
            {
                ...req,
            },
            { method: 'post' },
        )

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async updateLandingPageContent(
        req: UpdateLandingPageContentRequestBody,
    ): Promise<UpdateLandingPageContentResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<UpdateLandingPageContentResponse>
        >(
            API.UpdateLandingPageContent(),
            {
                ...req,
            },
            { method: 'post' },
        )

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async updateLandingPageLinks(
        req: UpdateLandingPageLinksBody,
    ): Promise<UpdateLandingPageLinksResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<UpdateLandingPageLinksResponse>
        >(
            API.UpdateLandingPageLinks(),
            {
                ...req,
            },
            { method: 'post' },
        )

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async updateCartUpsellLinks(
        req: UpdateLandingPageCartUpsellsBody,
    ): Promise<UpdateLandingPageCartUpsellsResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<UpdateLandingPageCartUpsellsResponse>
        >(
            API.UpdateLandingPageCartUpsells(),
            {
                ...req,
            },
            { method: 'post' },
        )

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    // Landing Page Links
    async getAllLandingPageLinks(): Promise<GetAllLandingPageLinksResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetAllLandingPageLinksResponse>
        >(API.GetAllLandingPageLinks(), {}, { method: 'get' })

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async getLandingPageLink(
        linkHash: string,
    ): Promise<GetLandingPageLinkResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetLandingPageLinkResponse>
        >(API.GetLandingPageLink(linkHash), {}, { method: 'get' })

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async updateLandingPageLinkMetadata(
        req: UpdateLandingPageLinkRequestBody,
    ): Promise<UpdateLandingPageLinkResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<UpdateLandingPageLinkResponse>
        >(
            API.UpdateLandingPageLinkMetadata(),
            {
                ...req,
            },
            { method: 'post' },
        )

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async updateLandingPageLinkContent(
        bodyContent: UpdateLandingPageLinkContentRequest,
    ): Promise<UpdateLandingPageLinkContentResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<UpdateLandingPageLinkContentResponse>
        >(
            API.UpdateLandingPageLinkContent(),
            {
                ...bodyContent,
            },
            { method: 'post' },
        )

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async getAllProductsOrderedByRelevanceToMetaAd(
        metaAdId: string,
        req: GetAllProductsOrderedByRelevanceToMetaAdRequest,
    ): Promise<GetAllProductsOrderedByRelevanceToMetaAdResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetAllProductsOrderedByRelevanceToMetaAdResponse>
        >(
            API.GetAllProductsOrderedByRelevanceToMetaAd(metaAdId),
            {
                ...req,
            },
            { method: 'post' },
        )

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }
    async getAllShopifyProducts(
        req: GetClientAllShopifyProductsRequest,
    ): Promise<GetClientAllShopifyProductsResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetClientAllShopifyProductsResponse>
        >(
            API.GetAllShopifyProducts(),
            {
                ...req,
            },
            { method: 'post' },
        )

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getAllUnimportedShopifyProducts(
        queryParams: GetClientUnimportedShopifyProductsRequest,
    ): Promise<GetClientUnimportedShopifyProductsResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetClientUnimportedShopifyProductsResponse>
        >(API.GetAllUnimportedShopifyProducts(), queryParams, {
            method: 'post',
        })

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async importShopifyProduct(
        productShopifyId: string,
    ): Promise<ImportShopifyProductResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<ImportShopifyProductResponse>
        >(
            API.ImportShopifyProduct(),
            {
                shopifyProductId: productShopifyId,
            },
            { method: 'post' },
        )

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async resyncImagesForLandingPageLink(
        input: ResyncimagesForLandingPageLinkRequestBody,
    ): Promise<ResyncimagesForLandingPageLinkResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<ResyncimagesForLandingPageLinkResponse>
        >(API.ResyncImagesForLandingPageLink(), input, { method: 'post' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    // Images
    async uploadImages(files: File[]): Promise<UploadImagesResponse> {
        const formData = new FormData()
        files.forEach((file) => {
            formData.append(`files[]`, file, file.name)
        })

        if (!this.client) {
            throw new Error('No client')
        }

        const jwt = window.localStorage.getItem(LocalStorageKey.JWT)

        if (!jwt) {
            window.location.pathname = '/login'
        }

        const headers = {
            Authorization: `Bearer ${jwt}`,
        }

        const fullPath = `api/${this.client}/${API.UploadImages()}`

        const url = `${config('API_BASEURL')}/${fullPath}`

        const resp = await fetch(url, {
            method: 'post',
            headers,
            body: formData,
        })

        const response = await resp.json()

        if (resp.status == 401) {
            window.location.pathname = '/login'
        }

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }
        return response.result
    }

    async getAvailableClients(): Promise<GetAvailableClientsResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetAvailableClientsResponse>
        >(API.GetAvailableClients(), {}, { method: 'get' })

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async aiGenerateFromTemplate(
        input: CreateStorefrontsWithStorefrontsGeneratorV2Request,
    ): Promise<CreateStorefrontsWithStorefrontsGeneratorV2Response> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<CreateStorefrontsWithStorefrontsGeneratorV2Response>
        >(API.AiGenerateFromTemplate(), input, { method: 'post' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async retryAiGenerateFromTemplate(
        landingPageHash: string,
    ): Promise<CreateStorefrontsWithStorefrontsGeneratorResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<CreateStorefrontsWithStorefrontsGeneratorResponse>
        >(
            API.RetryAiGenerateFromTemplate(landingPageHash),
            {},
            { method: 'post' },
        )

        if (response.isError) {
            if (response.data?.noJobData) {
                throw new NoJobDataError(response.message, {
                    jobId: landingPageHash,
                })
            }
            throw new Error(response.message)
        }

        return response.result
    }

    async generateLandingPageFromTemplate(data: {
        landingPageHash?: string
        heroImage?: File
        heroVideo?: File
        name?: string
        productLinkHashes?: string[]
        instructions?: string
    }): Promise<void> {
        const body = new FormData()
        const { heroImage, heroVideo, ...jsonData } = data

        if (heroImage instanceof File) body.append('heroImage', heroImage)
        if (heroVideo instanceof File) body.append('heroVideo', heroVideo)

        body.append('data', JSON.stringify(jsonData))

        const headers = {
            Authorization: this.getBearerToken(),
        }

        const resp = await fetch(
            `${this.apiBaseUrl}/${API.GenerateLandingPageFromTemplate()}`,
            {
                method: 'post',
                headers,
                body,
            },
        )

        this.checkResponseAuth(resp)

        if (!resp.ok) {
            throw new Error('Failed to generate landing page from template', {
                cause: resp,
            })
        }
    }

    async getMetaAds(): Promise<MetaAd[]> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<MetaAdResponse>
        >(API.GetMetaAds(), {}, { method: 'get' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result.ads
    }

    async getTemplatesRankedByAi(
        input: GetTemplatesRankedInput,
    ): Promise<GetTemplatesRankedResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetTemplatesRankedResponse>
        >(API.GetTemplatesRankedByAi(), input, { method: 'post' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getShopifyProductById(
        shopifyId: string,
    ): Promise<AdminShopifyProduct> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<{ product: AdminShopifyProduct }>
        >(API.GetShopifyProductById(shopifyId), {}, { method: 'get' })

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result.product
    }

    async getExploCustomerToken(
        dashboardType?: ExploDashboardType,
    ): Promise<ExploCustomerTokenResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<ExploCustomerTokenResponse>
        >(API.GetExploCustomerToken(), dashboardType ? { dashboardType } : {}, {
            method: 'post',
        })

        if (response.isError) {
            console.log(response)
            throw new Error(response.message)
        }

        return response.result
    }

    async getFullFunnelAggregationMetrics(): Promise<FullFunnelAggregationsResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<FullFunnelAggregationsResponse>
        >(API.GetFullFunnelAggregations(), {}, { method: 'post' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getPerformanceInsights(): Promise<PerformanceInsightsResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<PerformanceInsightsResponse>
        >(API.GetPerformanceInsights(), {}, { method: 'get' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getStorefrontComparison(
        startDate?: Date,
        endDate?: Date,
    ): Promise<StorefrontComparisonResponse> {
        try {
            // Convert Date objects to ISO strings (YYYY-MM-DD) for API requests
            const startDateStr = startDate
                ? startDate.toISOString().split('T')[0]
                : undefined
            const endDateStr = endDate
                ? endDate.toISOString().split('T')[0]
                : undefined

            const response = await this.makeAuthenticatedRequest<
                ApiResponse<StorefrontComparisonResponse>
            >(
                API.GetStorefrontComparison(),
                { startDate: startDateStr, endDate: endDateStr },
                { method: 'get' },
            )

            if (response.isError) {
                throw new Error(response.message)
            }

            return response.result
        } catch (error) {
            console.error('Failed to get storefront comparison:', error)
            throw error
        }
    }

    async getStorefrontMetrics(landingPageHashes: string[], days: number) {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<StorefrontMetricsResponse>
        >(
            API.GetStorefrontMetrics(),
            { landingPageHashes, days },
            { method: 'post' },
        )

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getAllCrowLinks(): Promise<AdminCrowLink[]> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetAllCrowLinksResponse>
        >(API.GetAllCrowLinks(), {}, { method: 'get' })
        if (response.isError) {
            throw new Error(response.message)
        }
        return response.result.crowLinks
    }

    async getCrowLinksByLandingPage(
        landingPageHash: string,
    ): Promise<AdminCrowLink[]> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<GetCrowLinksByLandingPageResponse>
        >(API.GetCrowLinksByLandingPage(landingPageHash), {}, { method: 'get' })
        if (response.isError) {
            throw new Error(response.message)
        }
        return response.result.crowLinks
    }

    async createCrowLink(
        body: CreateCrowLinkRequest,
    ): Promise<CreateCrowLinkResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<CreateCrowLinkResponse>
        >(
            API.CreateCrowLink(),
            {
                ...body,
            },
            { method: 'post' },
        )
        if (response.isError) {
            throw new Error(response.message)
        }
        return response.result
    }

    async updateCrowLink(
        linkHash: string,
        body: CreateCrowLinkRequest,
    ): Promise<CreateCrowLinkResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<CreateCrowLinkResponse>
        >(
            API.UpdateCrowLink(linkHash),
            {
                ...body,
            },
            { method: 'post' },
        )
        if (response.isError) {
            throw new Error(response.message)
        }
        return response.result
    }

    async getRecentCrowLinkTestData(
        linkHash: string,
        experimentId: string,
    ): Promise<RecentCrowLinkTestDataResponse> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<RecentCrowLinkTestDataResponse>
        >(API.GetRecentCrowLinkTestData(linkHash, experimentId), undefined, {
            method: 'get',
        })

        if (response.isError) {
            throw new Error(response.message)
        }
        return response.result
    }

    async signup(email: string): Promise<{ success: boolean }> {
        const response = await fetch(`${config('API_BASEURL')}/auth/signup`, {
            method: 'post',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                username: email,
            }),
        })

        const data = await response.json()

        if (data.isError) {
            throw new Error(data.message || 'Failed to create account')
        }

        return data.result
    }

    async getAllPersonas(): Promise<{ personas: Persona[] }> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<{ personas: Persona[] }>
        >(API.GetAllPersonas(), {}, { method: 'get' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getPersonasRankedByAd(
        adId: string,
    ): Promise<{ personaResults: RankedPersonaResult[] }> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<{ personaResults: RankedPersonaResult[] }>
        >(API.GetPersonasRankedByAd(adId), {}, { method: 'get' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async createPersona(
        data: Omit<Persona, 'id' | 'createdAt' | 'updatedAt' | 'clientId'>,
    ): Promise<{ persona: Persona }> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<{ persona: Persona }>
        >(API.CreatePersona(), data, { method: 'post' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async updatePersona(
        id: number,
        data: Omit<Persona, 'id' | 'createdAt' | 'updatedAt' | 'clientId'>,
    ): Promise<{ persona: Persona }> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<{ persona: Persona }>
        >(API.UpdatePersona(id), data, { method: 'post' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async queuePersonaGeneration(
        userInput?: string,
    ): Promise<{ message: string }> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<{ message: string }>
        >(API.QueuePersonaGeneration(), { userInput }, { method: 'post' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async getPersonaGenerationStatus(): Promise<{
        status: string
        count: number
    }> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<{ status: string; count: number }>
        >(API.PersonaGenerationStatus(), {}, { method: 'get' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async deletePersona(id: number): Promise<void> {
        const response = await this.makeAuthenticatedRequest<ApiResponse<void>>(
            API.DeletePersona(id),
            {},
            { method: 'delete' },
        )

        if (response.isError) {
            throw new Error(response.message)
        }
    }

    async sendMagicLink(email: string) {
        return await fetch(`${config('API_BASEURL')}/auth/magic-link`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
        })
    }

    async verifyMagicLink(token: string) {
        return await fetch(`${config('API_BASEURL')}/auth/verify-magic-link`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token }),
        })
    }

    async inviteUser(data: {
        email: string
        role: string
    }): Promise<ApiResponse<any>> {
        return await this.makeAuthenticatedRequest<ApiResponse<any>>(
            'users/invite',
            data,
            { method: 'post' },
        )
    }

    async verifyInviteLink(token: string) {
        return await fetch(`${config('API_BASEURL')}/auth/verify-invite-link`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token }),
        })
    }

    async deleteUser(data: { email: string }): Promise<ApiResponse<any>> {
        const response = await this.makeAuthenticatedRequest<ApiResponse<any>>(
            API.DeleteUser(),
            data,
            { method: 'delete' },
        )

        if (response.isError) {
            throw new Error(response.message)
        }

        return response
    }

    async updateUser(data: {
        email: string
        role?: string
        // Add future user update fields here as needed
    }): Promise<ApiResponse<any>> {
        const response = await this.makeAuthenticatedRequest<ApiResponse<any>>(
            API.UpdateUser(),
            data,
            { method: 'post' },
        )

        if (response.isError) {
            throw new Error(response.message)
        }

        return response
    }

    // Keep this for backward compatibility, but it uses the more general updateUser method
    async updateUserRole(data: {
        email: string
        role: string
    }): Promise<ApiResponse<any>> {
        return this.updateUser(data)
    }

    async sendAppInstallEmail(email: string) {
        return await fetch(
            `${config('API_BASEURL')}/auth/send-app-install-email`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            },
        )
    }

    async syncMetaAccess(): Promise<ApiResponse<any>> {
        return await this.makeAuthenticatedRequest<ApiResponse<any>>(
            'client/sync-meta-access',
            {},
            { method: 'post' },
        )
    }

    async getFacebookAppInstallUrl(): Promise<{ installUrl: string }> {
        const response = await this.makeAuthenticatedRequest<
            ApiResponse<{ installUrl: string }>
        >(API.GetFacebookAppInstallUrl(), {}, { method: 'get' })

        if (response.isError) {
            throw new Error(response.message)
        }

        return response.result
    }

    async createCheckoutSession(): Promise<ApiResponse<{ url: string }>> {
        return this.makeAuthenticatedRequest<ApiResponse<{ url: string }>>(
            'subscription/checkout',
            {},
            { method: 'post' },
        )
    }

    async checkoutCompleted(data: { sessionId: string }) {
        return this.makeAuthenticatedRequest('subscription/completed', data, {
            method: 'post',
        })
    }

    async createBillingPortalSession(): Promise<ApiResponse<{ url: string }>> {
        return this.makeAuthenticatedRequest<ApiResponse<{ url: string }>>(
            'subscription/billing',
            {},
            { method: 'post' },
        )
    }

    async uploadReviewsCsv(file: File): Promise<void> {
        // Get presigned URL from backend
        const createUrlResponse = await this.makeAuthenticatedRequest<
            ApiResponse<{ uploadUrl: string; tempId: string }>
        >(API.UploadReviewsCsvCreateURL(), {}, { method: 'post' })

        if (createUrlResponse.isError) {
            throw new Error(createUrlResponse.message)
        }

        const { uploadUrl, tempId } = createUrlResponse.result

        // Upload file directly to S3 using presigned URL
        const uploadResponse = await fetch(uploadUrl, {
            method: 'put',
            body: file,
            headers: {
                'Content-Type': 'text/csv',
            },
        })

        if (!uploadResponse.ok) {
            throw new Error('Failed to upload file to S3')
        }

        // Process file
        const processFileResponse = await this.makeAuthenticatedRequest<
            ApiResponse<{ message: string }>
        >(API.ProcessReviewsCsv(), { tempId }, { method: 'post' })

        if (processFileResponse.isError) {
            throw new Error(processFileResponse.message)
        }
    }

    // Client Settings Endpoints
    async getClientSettings(
        clientHash: string,
    ): Promise<ApiResponse<GetClientSettingsResponse>> {
        return this.makeAuthenticatedRequest<
            ApiResponse<GetClientSettingsResponse>
        >(API.GetClientSettings(), { clientHash }, { method: 'get' })
    }

    async searchGoogleFonts(
        search: string,
    ): Promise<ApiResponse<SearchGoogleFontsResponse>> {
        return this.makeAuthenticatedRequest<
            ApiResponse<SearchGoogleFontsResponse>
        >(API.SearchGoogleFonts(), { search }, { method: 'get' })
    }

    async updateClientSettings(
        settings: Partial<ApiClientSettings>,
    ): Promise<ApiResponse<{ settings: ApiClientSettings }>> {
        return this.makeAuthenticatedRequest<
            ApiResponse<{ settings: ApiClientSettings }>
        >(API.UpdateClientSettings(), settings, { method: 'post' })
    }

    async updateGoogleFontFamily(
        fontType: 'primary' | 'secondary',
        fontFamily: string,
    ): Promise<ApiResponse<any>> {
        return this.makeAuthenticatedRequest<ApiResponse<any>>(
            API.UpdateGoogleFontFamily(),
            { fontType, fontFamily },
            { method: 'post' },
        )
    }

    // Brand Guidelines
    async createBrandGuideline(
        content: string,
    ): Promise<ApiResponse<CreateBrandGuidelineResponse>> {
        return this.makeAuthenticatedRequest<
            ApiResponse<CreateBrandGuidelineResponse>
        >(API.CreateBrandGuideline(), { content }, { method: 'post' })
    }

    async updateBrandGuideline(
        id: number,
        content: string,
    ): Promise<ApiResponse<UpdateBrandGuidelineResponse>> {
        return this.makeAuthenticatedRequest<
            ApiResponse<UpdateBrandGuidelineResponse>
        >(API.UpdateBrandGuideline(id), { content }, { method: 'post' })
    }

    async deleteBrandGuideline(
        id: number,
    ): Promise<ApiResponse<DeleteBrandGuidelineResponse>> {
        return this.makeAuthenticatedRequest<
            ApiResponse<DeleteBrandGuidelineResponse>
        >(API.DeleteBrandGuideline(id), {}, { method: 'delete' })
    }

    // Brand Benefits
    async bulkReplaceBrandBenefits(
        request: BulkUpdateBrandBenefitsRequest,
    ): Promise<ApiResponse<GetBrandBenefitsResponse>> {
        return this.makeAuthenticatedRequest<
            ApiResponse<GetBrandBenefitsResponse>
        >(
            API.BulkReplaceBrandBenefits(),
            { brandBenefits: request.brandBenefits },
            { method: 'post' },
        )
    }

    async getSlackChannelInvite(): Promise<
        ApiResponse<SlackChannelWithShareLink>
    > {
        return R.makeAuthenticatedRequest<
            ApiResponse<SlackChannelWithShareLink>
        >(
            API.GetSlackChannelInvite(),
            {}, // No parameters needed - backend uses client from URL path
            { method: 'POST' },
            false, // Use standard client-aware path pattern
        )
    }
}

export const R = Requestor.getInstance()
